import { format, parseISO } from 'date-fns';
import type { ContactsTableColumnDef } from '../config/contacts-table-columns';
import { parseCustomAttributeColumnKey } from '../config/contacts-table-columns';
import type { CustomFieldDefinition } from '../types/custom-field-definitions.types';
import type { Contact } from '../types/contacts.types';
import { getContactDisplayName, getContactLists } from './contact-attributes';
import { getContactCustomFieldDisplayValue } from './custom-field-attributes.utils';

export const CONTACT_EXPORT_CSV_HEADERS = [
  'Name',
  'Email',
  'Phone',
  'Email status',
  'SMS status',
  'Source',
  'Lists',
  'Created',
] as const;

export type ContactExportCsvOptions = {
  /** When set, CSV columns follow this table column order/visibility. */
  columns?: ContactsTableColumnDef[];
  customFieldDefinitions?: CustomFieldDefinition[];
};

type ExportField = {
  header: string;
  getValue: (contact: Contact) => string;
};

export const escapeCsvCell = (value: string): string => {
  // Neutralize spreadsheet formula injection (=, +, -, @) before quoting.
  const safe =
    value.length > 0 && /^[=+\-@]/.test(value) ? `'${value}` : value;
  const needsQuoting =
    safe.includes('"') || safe.includes(',') || safe.includes('\n') || safe.includes('\r');
  if (!needsQuoting) return safe;
  return `"${safe.replace(/"/g, '""')}"`;
};

export const formatContactExportCreatedDate = (value?: string | null): string => {
  if (!value) return '';
  try {
    return format(parseISO(value), 'MMM d, yyyy');
  } catch {
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return '';
    return format(date, 'MMM d, yyyy');
  }
};

const defaultExportFields = (): ExportField[] => [
  { header: 'Name', getValue: (c) => getContactDisplayName(c) },
  { header: 'Email', getValue: (c) => c.Email ?? '' },
  { header: 'Phone', getValue: (c) => c.Phone ?? '' },
  { header: 'Email status', getValue: (c) => c.EmailSubscriptionStatus ?? '' },
  { header: 'SMS status', getValue: (c) => c.SmsSubscriptionStatus ?? '' },
  { header: 'Source', getValue: (c) => c.Source ?? '' },
  { header: 'Lists', getValue: (c) => getContactLists(c).join(';') },
  { header: 'Created', getValue: (c) => formatContactExportCreatedDate(c.CreatedDate) },
];

export const resolveContactExportFields = (
  columns?: ContactsTableColumnDef[],
  customFieldDefinitions: CustomFieldDefinition[] = []
): ExportField[] => {
  if (!columns || columns.length === 0) {
    return defaultExportFields();
  }

  const fields: ExportField[] = [];

  for (const column of columns) {
    const attributeKey = parseCustomAttributeColumnKey(column.id);
    if (attributeKey) {
      const definition =
        customFieldDefinitions.find((field) => field.Key === attributeKey) ??
        ({
          Key: attributeKey,
          Label: column.label,
          DataType: 'Text',
        } as CustomFieldDefinition);

      fields.push({
        header: column.label,
        getValue: (contact) => getContactCustomFieldDisplayValue(contact, definition) ?? '',
      });
      continue;
    }

    switch (column.id) {
      case 'name':
        fields.push({ header: 'Name', getValue: (c) => getContactDisplayName(c) });
        break;
      case 'email':
        // Email column UI also shows subscription status; keep statuses with Email.
        fields.push({ header: 'Email', getValue: (c) => c.Email ?? '' });
        fields.push({
          header: 'Email status',
          getValue: (c) => c.EmailSubscriptionStatus ?? '',
        });
        fields.push({
          header: 'SMS status',
          getValue: (c) => c.SmsSubscriptionStatus ?? '',
        });
        break;
      case 'phone':
        fields.push({ header: 'Phone', getValue: (c) => c.Phone ?? '' });
        break;
      case 'source':
        fields.push({ header: 'Source', getValue: (c) => c.Source ?? '' });
        break;
      case 'lists':
        fields.push({ header: 'Lists', getValue: (c) => getContactLists(c).join(';') });
        break;
      case 'created':
        fields.push({
          header: 'Created',
          getValue: (c) => formatContactExportCreatedDate(c.CreatedDate),
        });
        break;
      case 'language':
        fields.push({
          header: 'Language',
          getValue: (c) => c.Locale?.trim() || c.Language?.trim() || '',
        });
        break;
      default:
        break;
    }
  }

  return fields.length > 0 ? fields : defaultExportFields();
};

export const contactToExportCsvRow = (
  contact: Contact,
  options?: ContactExportCsvOptions
): string[] => {
  const fields = resolveContactExportFields(
    options?.columns,
    options?.customFieldDefinitions
  );
  return fields.map((field) => field.getValue(contact));
};

export const buildContactsExportCsv = (
  contacts: Contact[],
  options?: ContactExportCsvOptions
): string => {
  const fields = resolveContactExportFields(
    options?.columns,
    options?.customFieldDefinitions
  );
  const headers = fields.map((field) => field.header);
  const lines = [
    headers.join(','),
    ...contacts.map((contact) =>
      fields.map((field) => escapeCsvCell(field.getValue(contact))).join(',')
    ),
  ];
  return lines.join('\r\n');
};

export const buildContactsExportFilename = (date: Date = new Date()): string => {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  return `contacts-export-${y}-${m}-${d}.csv`;
};

export const downloadContactsExportCsv = (csv: string, filename?: string): void => {
  const name = filename ?? buildContactsExportFilename();
  // UTF-8 BOM so Excel opens non-ASCII fields correctly.
  const blob = new Blob([`\uFEFF${csv}`], { type: 'text/csv;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement('a');
  anchor.href = url;
  anchor.download = name;
  anchor.style.display = 'none';
  document.body.appendChild(anchor);
  anchor.click();
  document.body.removeChild(anchor);
  URL.revokeObjectURL(url);
};
